import { Calendar, ExternalLink, XCircle } from 'lucide-react'
import { Link } from 'react-router-dom'
import { StatusBadge } from '@/components/training/StatusBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Progress } from '@/components/ui/progress'
import { hasFollowUpSheet } from '@/lib/skill-check'
import { useTenantPath } from '@/lib/tenant'

interface EnrollmentCardProps {
  id: string
  trainingId: string
  trainingTitle: string
  status: string
  enrolledAt?: string | null
  completedAt?: string | null
  followUpProgress?: { completed: number; total: number }
  onCancel?: (enrollmentId: string) => void
  isCancelling?: boolean
}

export function EnrollmentCard({
  id,
  trainingId,
  trainingTitle,
  status,
  enrolledAt,
  followUpProgress,
  onCancel,
  isCancelling,
}: EnrollmentCardProps) {
  const tp = useTenantPath()
  const canCancel = status === 'applied' || status === 'pending_approval' || status === 'confirmed'
  const showFollowUp = hasFollowUpSheet(status)

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <Link to={tp(`/trainings/${trainingId}`)}>
            <CardTitle className="text-base hover:text-emerald-600 transition-colors">
              {trainingTitle}
            </CardTitle>
          </Link>
          <StatusBadge type="enrollment" status={status} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {enrolledAt && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            <span>申込日: {new Date(enrolledAt).toLocaleDateString('ja-JP')}</span>
          </div>
        )}

        {showFollowUp && followUpProgress && followUpProgress.total > 0 && (
          <div className="space-y-1">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>スキルチェック</span>
              <span>
                {followUpProgress.completed}/{followUpProgress.total}
              </span>
            </div>
            <Progress
              value={(followUpProgress.completed / followUpProgress.total) * 100}
              className="h-2"
            />
          </div>
        )}

        <div className="flex gap-2">
          {showFollowUp && (
            <Link to={tp(`/follow-ups/${id}`)} className="flex-1">
              <Button variant="outline" size="sm" className="w-full">
                <ExternalLink className="mr-1 h-3.5 w-3.5" />
                スキルチェック
              </Button>
            </Link>
          )}
          {canCancel && onCancel && (
            <Button
              variant="ghost"
              size="sm"
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => onCancel(id)}
              disabled={isCancelling}
            >
              <XCircle className="mr-1 h-3.5 w-3.5" />
              キャンセル
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
